import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { atom } from 'jotai';
import useSites from '../../hooks/useSites';
import { Site } from '../../CommonTypes';

import { buildEmbedSnippet } from './utils';
import { DemoWidget } from './DemoWidget';
import { CodeBlock } from './CodeBlock';
import useAvailableFeatures from '../../hooks/useAvailableFeatures';

export const selectedViewAtom = atom<string>('');
export const tabAtom = atom<Site | undefined>(undefined);
export const embedTypeAtom = atom<'widget' | 'iframe' | 'link'>('widget');

export default function Guide() {
  const [sites] = useSites();
  const [selectedSite, setSelectedSite] = useAtom(tabAtom);
  const [selectedView, setSelectedView] = useAtom(selectedViewAtom);
  const [embedType, setEmbedType] = useAtom(embedTypeAtom);

  const company = selectedSite?.company ?? sites[0]?.company;
  const sitename = selectedSite?.sitename ?? sites[0]?.sitename;

  const [haveSolar, haveMaps, haveGallery] = useAvailableFeatures({ company, sitename });

  useEffect(() => {
    if (selectedView === 'solar') {
      setSelectedView(haveSolar ? 'solar' : '');
    } else if (selectedView === 'map') {
      setSelectedView(haveMaps ? 'map' : '');
    } else if (selectedView === 'panorama') {
      setSelectedView(haveGallery ? 'panorama' : '');
    }
  }, [selectedView, haveSolar, haveMaps, haveGallery]);

  if (!company) return null;

  const snippet = buildEmbedSnippet({
    company,
    sitename,
    view: selectedView || '',
    type: embedType,
  });

  const onPickSite = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    const site = sites.find(s => `${s.company}/${s.sitename}` === id);
    setSelectedSite(site);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <section id="guide" className="mb-8 scroll-mt-56">
        <h2 className="text-xl font-semibold">Guide</h2>
        <p className="text-slate-300">
          Select Project, View and Embed Type. Then copy the embed code and add it to your website. For options, see
          Reference Tab.
        </p>
      </section>

      <section className="mb-8" aria-labelledby="config-heading">
        <h3 id="config-heading" className="mb-2 text-lg font-medium">
          Configuration
        </h3>

        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-left md:min-w-[600px]">
            <tbody className="divide-y divide-slate-800">
              <tr>
                <td className="px-3 py-2 font-semibold md:w-[220px]">Project</td>
                <td className="px-3 py-2">
                  <select
                    className="w-40 rounded-md border border-slate-700 bg-[#0f1116] px-2 py-1"
                    value={selectedSite ? `${selectedSite.company}/${selectedSite.sitename}` : `${company}/${sitename}`}
                    onChange={onPickSite}
                  >
                    {sites.map(s => {
                      if (s.disabled) return null;
                      return (
                        <option key={`${s.company}/${s.sitename}`} value={`${s.company}/${s.sitename}`}>
                          {s.sitename}
                        </option>
                      );
                    })}
                  </select>
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">View</td>
                <td className="px-3 py-2">
                  <select
                    className="w-40 rounded-md border border-slate-700 bg-[#0f1116] px-2 py-1"
                    value={selectedView}
                    onChange={e => setSelectedView(e.target.value)}
                  >
                    <option value="">Bostadsväljare</option>
                    {haveMaps && <option value="map">Karta</option>}
                    {haveSolar && <option value="solar">Solstudie</option>}
                    {haveGallery && <option value="panorama">Panorama</option>}
                    <option value="table">Tabell</option>
                  </select>
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-semibold">Embed type</td>
                <td className="px-3 py-2">
                  <select
                    className="w-40 rounded-md border border-slate-700 bg-[#0f1116] px-2 py-1"
                    value={embedType}
                    onChange={e => setEmbedType(e.target.value as 'widget' | 'iframe' | 'link')}
                  >
                    <option value="widget">Widget</option>
                    <option value="iframe">Iframe</option>
                    <option value="link">Link</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-8" aria-labelledby="embed-heading">
        <h3 id="embed-heading" className="mb-2 scroll-mt-32 text-lg font-medium">
          Embed code
        </h3>
        <CodeBlock code={snippet} language="html" ariaLabel="Embed snippet" />
      </section>

      <section className="mb-8" aria-labelledby="preview-heading">
        <h3 id="preview-heading" className="mb-2 scroll-mt-32 text-lg font-medium">
          Preview
        </h3>
        <div className="bg-white p-0 md:rounded-xl md:border md:border-slate-800 md:p-4">
          <DemoWidget company={company} sitename={sitename} view={selectedView || undefined} embedType={embedType} />
        </div>
      </section>
    </div>
  );
}
